import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  Plus,
  BookOpen,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { PlaybookShort } from "../model/Playbook";
import { GeneratedPlaybook } from "../models/GeneratedPlaybook";
import { useSearchPlaybooks } from "../api/useSearchPlaybooks";
import { useCreatePlaybook } from "../api/useCreatePlaybook";
import { useGeneratePlaybook } from "../api/useGeneratePlaybook";
import { PlaybookCreatorModal } from "./PlaybookCreatorModal";
import { PlaybookGenerateModal } from "./PlaybookGenerateModal";
import { PlaybookCreationTypeModal } from "./PlaybookCreationTypeModal";
import { PlaybookPreviewModal } from "./PlaybookPreviewModal";
import { useNavigate } from "react-router-dom";
import { PlaybookCardv2 } from "./PlaybookCardv2";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";
import { PlaybookDetailLevel } from "../models/PlaybookDetailLevel";
import { useMarkPlaybookAsFavorite } from "../api/useMarkPlaybookAsFavorite";
import { useUnmarkPlaybookAsFavorite } from "../api/useUnmarkPlaybookAsFavorite";
import { PlaybookType } from "../models/PlaybookType";
import { useDebouncedValue } from "@mantine/hooks";

interface PlaybookListProps {
  setActivePlaybook: (playbook: PlaybookShort | undefined) => Promise<void>;
}

export default function PlaybookList({ setActivePlaybook }: PlaybookListProps) {
  const { playbooks, searchPlaybooks } = useSearchPlaybooks();
  const { createPlaybook, createdPlaybook } = useCreatePlaybook();
  const { markPlaybookAsFavorite } = useMarkPlaybookAsFavorite();
  const { unmarkPlaybookAsFavorite } = useUnmarkPlaybookAsFavorite();

  const { generatePlaybook, generatedPlaybook, isGenerating } =
    useGeneratePlaybook();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
  const [showCreationTypeModal, setShowCreationTypeModal] = useState(false);
  const [showManualCreator, setShowManualCreator] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [generatedPlaybookData, setGeneratedPlaybookData] =
    useState<GeneratedPlaybook | null>(null);
  const [showAllSystemPlaybooks, setShowAllSystemPlaybooks] = useState(false);
  const [showAllUserPlaybooks, setShowAllUserPlaybooks] = useState(false);
  const [userPlaybooks, setUserPlaybooks] = useState<PlaybookShort[]>([]);
  const [systemPlaybooks, setSystemPlaybooks] = useState<PlaybookShort[]>([]);
  const navigate = useNavigate();

  const [generateName, setGenerateName] = useState("");
  const [generatePurpose, setGeneratePurpose] = useState("");
  const [generateDetailLevel, setGenerateDetailLevel] = useState(
    PlaybookDetailLevel.Normal,
  );

  useEffect(() => {
    searchPlaybooks({ query: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (generatedPlaybook) {
      setGeneratedPlaybookData(generatedPlaybook);
      setShowGenerator(false); // Only close generator modal after successful generation
      setShowPreview(true);
    }
  }, [generatedPlaybook]);

  useEffect(() => {
    if (!playbooks) return;
    setUserPlaybooks(
      playbooks.filter((playbook) => playbook.type === PlaybookType.User),
    );
    setSystemPlaybooks(
      playbooks.filter((playbook) => playbook.type === PlaybookType.System),
    );
  }, [playbooks]);

  useEffect(() => {
    if (createdPlaybook) {
      navigate(`/playbooks/${createdPlaybook.id}/edit`);
    }
  }, [createdPlaybook, navigate]);

  const handleStarClick = async (playbookId: string, starred: boolean) => {
    if (starred) {
      await markPlaybookAsFavorite(playbookId);
    } else {
      await unmarkPlaybookAsFavorite(playbookId);
    }

    setUserPlaybooks((prevPlaybooks) =>
      prevPlaybooks.map((playbook) =>
        playbook.id === playbookId
          ? { ...playbook, isFavorite: starred }
          : playbook,
      ),
    );

    setSystemPlaybooks((prevPlaybooks) =>
      prevPlaybooks.map((playbook) =>
        playbook.id === playbookId
          ? { ...playbook, isFavorite: starred }
          : playbook,
      ),
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto max-w-7xl px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <BookOpen className="h-6 w-6" />
                <h1 className="text-2xl font-bold text-gray-900">
                  Playbook Manager
                </h1>
              </div>
            </div>
            <Button
              onClick={() => setShowCreationTypeModal(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Playbook
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl px-8 py-12">
        {/* Search */}
        <div className="mb-8">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search playbooks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* User playbooks */}
        {userPlaybooks.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Your Playbooks
              </h2>
              {userPlaybooks.length > 4 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllUserPlaybooks(!showAllUserPlaybooks)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                >
                  {showAllUserPlaybooks ? (
                    <>
                      Show Less
                      <ChevronUp className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show All
                      <ChevronDown className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(showAllUserPlaybooks
                ? userPlaybooks
                : userPlaybooks.slice(0, 4)
              ).map((playbook) => (
                <PlaybookCardv2
                  key={playbook.id}
                  playbook={playbook}
                  isEditable={true}
                  setActivePlaybook={setActivePlaybook}
                  onDelete={() => {
                    searchPlaybooks({ query: debouncedSearch });
                  }}
                  onStar={handleStarClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Default Playbooks */}
        {systemPlaybooks.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                Quantly Playbooks
              </h2>
              {systemPlaybooks.length > 4 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setShowAllSystemPlaybooks(!showAllSystemPlaybooks)
                  }
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                >
                  {showAllSystemPlaybooks ? (
                    <>
                      Show Less
                      <ChevronUp className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show All
                      <ChevronDown className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(showAllSystemPlaybooks
                ? systemPlaybooks
                : systemPlaybooks.slice(0, 4)
              ).map((playbook) => (
                <PlaybookCardv2
                  key={playbook.id}
                  playbook={playbook}
                  setActivePlaybook={setActivePlaybook}
                  onStar={handleStarClick}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {userPlaybooks.length === 0 && systemPlaybooks.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No playbooks found</p>
          </div>
        )}
      </div>

      {showCreationTypeModal && (
        <PlaybookCreationTypeModal
          open={showCreationTypeModal}
          onClose={() => setShowCreationTypeModal(false)}
          onSelectManual={() => {
            setShowCreationTypeModal(false);
            setShowManualCreator(true);
          }}
          onSelectGenerate={() => {
            setShowCreationTypeModal(false);
            setShowGenerator(true);
          }}
        />
      )}

      {showManualCreator && (
        <PlaybookCreatorModal
          open={showManualCreator}
          onClose={() => setShowManualCreator(false)}
          onCreateManual={async (name, description) => {
            setShowManualCreator(false);
            await createPlaybook(name, [], description);
            await searchPlaybooks({ query: debouncedSearch });
          }}
        />
      )}

      {showGenerator && (
        <PlaybookGenerateModal
          open={showGenerator}
          onClose={() => {
            setShowGenerator(false);
            setGeneratedPlaybookData(null);
            setGenerateName("");
            setGeneratePurpose("");
            setGenerateDetailLevel(PlaybookDetailLevel.Normal);
          }}
          onGenerate={async () => {
            try {
              await generatePlaybook(
                generateName.trim(),
                generatePurpose.trim(),
                generateDetailLevel,
              );
            } catch (_) {
              toast({
                title: "Error",
                description: "Failed to generate playbook. Please try again.",
                variant: "destructive",
              });
            }
          }}
          isGenerating={isGenerating}
          name={generateName}
          setName={setGenerateName}
          purpose={generatePurpose}
          setPurpose={setGeneratePurpose}
          detailLevel={generateDetailLevel}
          setDetailLevel={setGenerateDetailLevel}
        />
      )}

      {showPreview && generatedPlaybookData && (
        <PlaybookPreviewModal
          open={showPreview}
          playbook={generatedPlaybookData}
          onClose={() => {
            setShowPreview(false);
            setShowGenerator(true);
            setGeneratedPlaybookData(null);
          }}
          onConfirm={async () => {
            try {
              const stepsWithIds = generatedPlaybookData.steps.map(
                (step, index) => ({
                  ...step,
                  id: uuidv4(),
                  order: index,
                }),
              );

              await createPlaybook(
                generatedPlaybookData.title,
                stepsWithIds,
                generatedPlaybookData.description,
              );
              setGeneratedPlaybookData(null);
              setShowPreview(false);
              toast({
                title: "Success",
                description: "Playbook created successfully!",
              });
              setGenerateName("");
              setGeneratePurpose("");
              setGenerateDetailLevel(PlaybookDetailLevel.Normal);
              await searchPlaybooks({ query: debouncedSearch });
              // Navigation will be handled by useEffect when createdPlaybook updates
            } catch (_) {
              setShowPreview(true);
              toast({
                title: "Error",
                description: "Failed to create playbook. Please try again.",
                variant: "destructive",
              });
            }
          }}
        />
      )}
    </div>
  );
}
